/**
 * Horas del día e instantes (design.md, sección 6).
 *
 * - HoraLocal 'HH:MM' (24 h): la hora que el usuario captura o ve.
 * - Instante: ISO en UTC ('…Z'), el momento exacto que se guarda.
 *
 * Regla: las duraciones se calculan entre instantes, nunca restando horas. Así
 * un ayuno de 8:10 p. m. a 8:15 a. m. del día siguiente mide 12 h 5 min.
 *
 * Complementa a dates.ts, que maneja días de calendario.
 */

import { aFechaLocal, esFechaLocal, type FechaLocal } from './dates';
import { ErrorValidacion } from './errors';

/** Hora local en formato 'HH:MM' de 24 horas. */
export type HoraLocal = string;

const PATRON_HORA = /^([01]\d|2[0-3]):([0-5]\d)$/;
const PATRON_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
const MILISEGUNDOS_POR_MINUTO = 60_000;

export function esHoraLocal(valor: unknown): valor is HoraLocal {
  return typeof valor === 'string' && PATRON_HORA.test(valor);
}

export function exigirHoraLocal(valor: unknown, campo = 'hora'): HoraLocal {
  if (!esHoraLocal(valor)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser una hora válida con formato HH:MM.`,
      campo,
    );
  }
  return valor;
}

const dos = (n: number) => String(n).padStart(2, '0');

/** Hora local de un Date, con getters locales. */
export function aHoraLocal(fecha: Date): HoraLocal {
  return `${dos(fecha.getHours())}:${dos(fecha.getMinutes())}`;
}

/** Hora local actual, para proponerla al capturar (Req. 15.2, 18.7). */
export function horaActual(ahora: Date = new Date()): HoraLocal {
  return aHoraLocal(ahora);
}

/**
 * Instante de una fecha y hora locales. Se construye con los componentes
 * locales, nunca parseando 'AAAA-MM-DD' (que JavaScript toma como UTC).
 */
export function instanteLocal(fecha: FechaLocal, hora: HoraLocal): string {
  const partesFecha = esFechaLocal(fecha) ? PATRON_FECHA.exec(fecha) : null;
  const partesHora = PATRON_HORA.exec(hora);
  if (partesFecha === null || partesHora === null) {
    throw new ErrorValidacion(`Fecha u hora inválida: "${fecha} ${hora}".`);
  }
  const momento = new Date(
    Number(partesFecha[1]),
    Number(partesFecha[2]) - 1,
    Number(partesFecha[3]),
    Number(partesHora[1]),
    Number(partesHora[2]),
    0,
    0,
  );
  return momento.toISOString();
}

function leerInstante(iso: string): Date {
  const marca = Date.parse(iso);
  if (Number.isNaN(marca)) {
    throw new ErrorValidacion(`Instante inválido: "${iso}".`);
  }
  return new Date(marca);
}

/** Día local en que ocurrió un instante. */
export function fechaLocalDe(iso: string): FechaLocal {
  return aFechaLocal(leerInstante(iso));
}

/** Hora local en que ocurrió un instante. */
export function horaLocalDe(iso: string): HoraLocal {
  return aHoraLocal(leerInstante(iso));
}

/**
 * El mismo instante N días antes o después, a la misma hora local (aunque en
 * medio cambie el horario de verano). Sirve para cambiar la fecha de un
 * entrenamiento sin cambiar su hora ni su duración.
 */
export function moverDias(iso: string, dias: number): string {
  if (!Number.isInteger(dias)) throw new ErrorValidacion('Los días deben ser un número entero.');
  const d = leerInstante(iso);
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + dias,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  ).toISOString();
}

/**
 * Minutos exactos (con fracción) de `desde` a `hasta`. Negativo si `hasta` es
 * anterior. No redondea: decidir si una meta se cumplió necesita el valor real.
 */
export function minutosEntre(desdeIso: string, hastaIso: string): number {
  return (leerInstante(hastaIso).getTime() - leerInstante(desdeIso).getTime()) /
    MILISEGUNDOS_POR_MINUTO;
}

/** Instante desplazado N minutos, por ejemplo la hora en que se cumple un ayuno. */
export function sumarMinutos(iso: string, minutos: number): string {
  return new Date(leerInstante(iso).getTime() + minutos * MILISEGUNDOS_POR_MINUTO).toISOString();
}

/**
 * "12 h 5 min", "45 min", "1 h". Trunca a minutos completos para no presentar
 * nunca más tiempo del que pasó: 11 h 59.9 min se muestra como 11 h 59 min.
 * Una duración negativa (dato inconsistente) se muestra como "0 min".
 */
export function formatearDuracion(minutos: number): string {
  const enteros = Number.isFinite(minutos) && minutos > 0 ? Math.floor(minutos) : 0;
  const horas = Math.floor(enteros / 60);
  const resto = enteros % 60;
  if (horas === 0) return `${resto} min`;
  if (resto === 0) return `${horas} h`;
  return `${horas} h ${resto} min`;
}

/** "8:10 a. m.", "12:00 p. m.", "12:05 a. m." */
export function formatearHora(hora: HoraLocal): string {
  const partes = PATRON_HORA.exec(hora);
  if (partes === null) throw new ErrorValidacion(`Hora inválida: "${hora}".`);
  const h = Number(partes[1]);
  const sufijo = h < 12 ? 'a. m.' : 'p. m.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${partes[2]} ${sufijo}`;
}

/** Hora presentable de un instante: "8:10 p. m." */
export function formatearHoraDeInstante(iso: string): string {
  return formatearHora(horaLocalDe(iso));
}
